import { useCallback, type ReactNode } from 'react';
import { useTextToSpeech } from '../../hooks/useTextToSpeech';
import { useVoicePrefs } from '../../lib/queries/useVoicePrefs';
import './SpeakButton.css';

/**
 * Small icon button that reads its `text` aloud via the coach's
 * configured TTS pipeline (system Web Speech or cloud OpenAI tts-1-hd).
 *
 * Brief principle #2 ("voice first wherever possible"): every Brain
 * output should be hear-able. Drop this on any text-bearing card —
 * session description, drill summary, last-week insight, severity
 * banner, etc. — so the coach on the touchline doesn't need to read.
 *
 * Hidden entirely when the coach has muted voice in Settings (no
 * disabled-button anti-pattern). Toggles between speak / stop while
 * the utterance is playing.
 */
interface SpeakButtonProps {
  text: string;
  /** Optional aria-label override. Default reads "Read this aloud". */
  ariaLabel?: string;
  /** Use the chunked sentence speaker for long bodies; tighter on short
   *  ones since the system engine handles them fine in one shot. */
  chunked?: boolean;
  /** Visual size — 'sm' for inline next to titles, 'md' for card actions. */
  size?: 'sm' | 'md';
  /** Render an explicit caption alongside the icon (e.g. "Listen"). */
  caption?: ReactNode;
}

export function SpeakButton({
  text,
  ariaLabel,
  chunked = false,
  size = 'sm',
  caption,
}: SpeakButtonProps) {
  const { prefs } = useVoicePrefs();
  const tts = useTextToSpeech({
    provider: prefs.voice_provider,
    rate: prefs.voice_rate,
    voiceId: prefs.voice_id,
  });

  const handleClick = useCallback(() => {
    if (tts.isSpeaking) {
      tts.stop();
      return;
    }
    if (chunked) tts.speakSentenceChunked(text);
    else tts.speak(text);
  }, [tts, text, chunked]);

  if (!prefs.voice_enabled) return null;
  if (!text || !text.trim()) return null;

  const label = ariaLabel ?? (tts.isSpeaking ? 'Stop reading' : 'Read this aloud');

  return (
    <button
      type="button"
      className="speak-btn"
      data-size={size}
      data-speaking={tts.isSpeaking || undefined}
      onClick={handleClick}
      aria-label={label}
      title={label}
    >
      <span className="speak-btn__glyph" aria-hidden>
        {tts.isSpeaking ? '⏹' : '🔊'}
      </span>
      {caption && <span className="speak-btn__caption">{caption}</span>}
    </button>
  );
}
